import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}/rbac`;

export default function OrganizationRolesTab() {
  const { permissions } = useAuth();
  const isSuperAdmin = permissions.has("*");

  const [roles, setRoles] = useState([]);
  const [allPerms, setAllPerms] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editingRole, setEditingRole] = useState(null);
  const [draftPerms, setDraftPerms] = useState([]);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------
  // 📡 Load roles + permissions
  // --------------------------------------------------
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("authToken");
        const headers = { Authorization: `Bearer ${token}` };

        const [rolesRes, permsRes] = await Promise.all([
          axios.get(`${API_BASE}/role`, { headers }),
          axios.get(`${API_BASE}/permissions`, { headers }),
        ]);

        setRoles(rolesRes.data || []);
        setAllPerms(permsRes.data || []);
      } catch (err) {
        setError(
          err?.response?.data?.error || "Failed to load roles and permissions",
        );
      } finally {
        setLoading(false);
      }
    };

    if (isSuperAdmin) load();
  }, [isSuperAdmin]);

  // --------------------------------------------------
  // 🔒 Access guard
  // --------------------------------------------------
  // if (!isSuperAdmin) {
  //   return (
  //     <Box sx={{ mt: 4, textAlign: "center" }}>
  //       <Typography variant="h6">Restricted Access</Typography>
  //       <Typography color="text.secondary">
  //         Only super administrators can manage roles and permissions.
  //       </Typography>
  //     </Box>
  //   );
  // }

  // --------------------------------------------------
  // ⏳ Global loader
  // --------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  // --------------------------------------------------
  // ❌ Error state
  // --------------------------------------------------
  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // --------------------------------------------------
  // ✏️ Editing helpers
  // --------------------------------------------------
  const startEdit = (role) => {
    setEditingRole(role.key);
    setDraftPerms(role.permissions || []);
  };

  const cancelEdit = () => {
    setEditingRole(null);
    setDraftPerms([]);
    setSaving(false);
  };

  const togglePerm = (key) => {
    setDraftPerms((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  // --------------------------------------------------
  // 💾 Save permissions
  // --------------------------------------------------
  const savePermissions = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("authToken");

      await axios.patch(
        `${API_BASE}/role/${editingRole}/permissions`,
        { permissions: draftPerms },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setRoles((prev) =>
        prev.map((r) =>
          r.key === editingRole ? { ...r, permissions: draftPerms } : r,
        ),
      );

      cancelEdit();
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to update permissions");
      setSaving(false);
    }
  };

  // --------------------------------------------------
  // 🖼️ Main render
  // --------------------------------------------------
  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        {roles.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No roles found. Please create roles first.
            </Alert>
          </Grid>
        )}

        {roles.map((role) => {
          const isEditing = editingRole === role.key;

          return (
            <Grid item xs={12} md={6} key={role.key}>
              <Card variant="outlined">
                <CardContent>
                  {/* HEADER */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6">{role.label}</Typography>

                    {!isEditing && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => startEdit(role)}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>

                  <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {role.description}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  {/* VIEW MODE */}
                  {!isEditing ? (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(role.permissions || []).length === 0 ? (
                        <Typography fontSize={13} color="text.secondary">
                          No permissions assigned
                        </Typography>
                      ) : (
                        role.permissions.map((p) => (
                          <Chip key={p} label={p} size="small" />
                        ))
                      )}
                    </Box>
                  ) : (
                    <>
                      {/* EDIT MODE */}
                      <Stack
                        spacing={1}
                        sx={{ maxHeight: 300, overflow: "auto" }}
                      >
                        {allPerms.map((perm) => (
                          <FormControlLabel
                            key={perm.key}
                            control={
                              <Checkbox
                                checked={draftPerms.includes(perm.key)}
                                onChange={() => togglePerm(perm.key)}
                                disabled={saving}
                              />
                            }
                            label={perm.key}
                          />
                        ))}
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      {/* ACTIONS */}
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Button onClick={cancelEdit} disabled={saving}>
                          Cancel
                        </Button>

                        <Button
                          variant="contained"
                          onClick={savePermissions}
                          disabled={saving}
                          startIcon={
                            saving ? <CircularProgress size={16} /> : null
                          }
                        >
                          Save
                        </Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
